/**
 * Pure billing-config helpers extracted from BillingSetupSection so they can
 * be unit-tested without a React/MUI runtime. Both the inline section and
 * the legacy modal re-export from this module — a single source of truth
 * for defaults, validation, and payload shaping.
 */
import dayjs from "dayjs";

export type BillingType = "monthly" | "per_class" | "period_based";

export type BillingConfig = {
  class_id: number;
  billing_type: BillingType;
  fee_amount: string;
  billing_start_date: string;
  billing_day: number;
  first_month_proration: "full" | "prorate" | "next";
  invoice_generation_preference: "now" | "next";
  period_start_date: string;
  period_end_date: string;
  period_total_fee: string;
  period_installment_count: number;
  /**
   * Explicit user acknowledgement that this class's billing is set up. Never
   * derived from field values — the admin must press "Complete Billing Setup"
   * on the card. Any subsequent field edit flips this back to false so the
   * user always sees + confirms the current numbers before assigning.
   */
  is_completed: boolean;
};

export const isValidMoney = (v: string) =>
  v !== "" && !Number.isNaN(Number(v)) && Number(v) >= 0;

export const emptyConfig = (class_id: number): BillingConfig => ({
  class_id,
  billing_type: "monthly",
  fee_amount: "",
  billing_start_date: dayjs().format("YYYY-MM-DD"),
  billing_day: 1,
  first_month_proration: "full",
  invoice_generation_preference: "next",
  period_start_date: dayjs().format("YYYY-MM-DD"),
  period_end_date: dayjs().add(3, "month").format("YYYY-MM-DD"),
  period_total_fee: "",
  period_installment_count: 1,
  is_completed: false,
});

/**
 * Format a day-of-month as an English ordinal, e.g. 22 → "22nd". Kept here
 * (not in the view) so tests and any summary component can reuse it.
 */
export const dayOrdinal = (n: number): string => {
  const j = n % 10;
  const k = n % 100;
  if (k >= 11 && k <= 13) return `${n}th`;
  if (j === 1) return `${n}st`;
  if (j === 2) return `${n}nd`;
  if (j === 3) return `${n}rd`;
  return `${n}th`;
};

/** Per-config validity check used by the parent to gate the Assign button. */
export const isConfigValid = (cfg: BillingConfig): boolean => {
  if (cfg.billing_type === "period_based") {
    if (!cfg.period_start_date || !cfg.period_end_date) return false;
    if (dayjs(cfg.period_end_date).isBefore(dayjs(cfg.period_start_date))) return false;
    if (!isValidMoney(cfg.period_total_fee) || Number(cfg.period_total_fee) <= 0) return false;
    if (!cfg.period_installment_count || cfg.period_installment_count < 1 || cfg.period_installment_count > 24) return false;
    return true;
  }
  if (!isValidMoney(cfg.fee_amount)) return false;
  if (!cfg.billing_start_date) return false;
  if (cfg.billing_type === "monthly") {
    if (!cfg.billing_day || cfg.billing_day < 1 || cfg.billing_day > 28) return false;
  }
  return true;
};

/**
 * Shape one config row into the backend payload. Only the fields relevant to
 * the selected billing_type are populated so the API validator applies its
 * per-type rules. Modal and inline flows share this identical builder.
 */
export const buildPayloadRow = (c: BillingConfig) => {
  if (c.billing_type === "period_based") {
    return {
      class_id: c.class_id,
      billing_type: c.billing_type,
      billing_start_date: c.period_start_date,
      period_start_date: c.period_start_date,
      period_end_date: c.period_end_date,
      period_total_fee: Number(c.period_total_fee),
      period_installment_count: c.period_installment_count || 1,
    };
  }
  return {
    class_id: c.class_id,
    billing_type: c.billing_type,
    fee_amount: Number(c.fee_amount),
    billing_start_date: c.billing_start_date,
    billing_day: c.billing_type === "monthly" ? c.billing_day : undefined,
    first_month_proration: c.billing_type === "monthly" ? c.first_month_proration : undefined,
  };
};
